import React from "react";
import { Editor } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
  SeparatorHorizontal,
  Eraser,
} from "lucide-react";
import ColorPicker from "./ColorPicker";
import ImageUrlButton from "./ImageUrlButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMAIL_SAFE_FONTS = [
  { name: "Arial", value: "Arial, sans-serif" },
  { name: "Verdana", value: "Verdana, sans-serif" },
  { name: "Georgia", value: "Georgia, serif" },
  { name: "Times New Roman", value: "Times New Roman, serif" },
  { name: "Tahoma", value: "Tahoma, sans-serif" },
];

const EMAIL_SAFE_FONT_SIZES = [
  { name: "8pt", value: "8pt" },
  { name: "10pt", value: "10pt" },
  { name: "12pt", value: "12pt" },
  { name: "14pt", value: "14pt" },
  { name: "16pt", value: "16pt" },
  { name: "18pt", value: "18pt" },
  { name: "24pt", value: "24pt" },
  { name: "36pt", value: "36pt" },
];

interface TipTapToolbarProps {
  editor: Editor | null;
}

const TipTapToolbar: React.FC<TipTapToolbarProps> = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const setLink = () => {
    const url = window.prompt("URL");
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const resetFormatting = () => {
    editor.chain()
      .focus()
      .clearNodes()
      .unsetAllMarks()
      .run();
  };

  const setFontSize = (size: string) => {
    editor.chain().focus().setMark('textStyle', { fontSize: size }).run();
  };

  React.useEffect(() => {
    if (editor) {
      const handleFocus = () => {
        const editorElement = document.querySelector('.tiptap-editor-content');
        if (editorElement && editor.isEmpty) {
          editorElement.setAttribute('data-placeholder', 'Type your content here...');
          editorElement.classList.add('is-empty');
        }
      };
      
      const handleBlur = () => {
        const editorElement = document.querySelector('.tiptap-editor-content');
        if (editorElement) {
          editorElement.removeAttribute('data-placeholder');
          editorElement.classList.remove('is-empty');
        }
      };
      
      editor.on('focus', handleFocus);
      editor.on('blur', handleBlur);
      
      const editorElement = document.querySelector('.tiptap-editor-content');
      if (editorElement && editor.isEmpty) {
        editorElement.setAttribute('data-placeholder', 'Type your content here...');
        editorElement.classList.add('is-empty');
      }
      
      return () => {
        editor.off('focus', handleFocus);
        editor.off('blur', handleBlur);
      };
    }
  }, [editor]);

  return (
    <div className="border-b p-2 flex flex-wrap gap-1">
      <Select
        value={editor.getAttributes('textStyle').fontFamily || "Arial, sans-serif"}
        onValueChange={(value) => editor.chain().focus().setFontFamily(value).run()}
      >
        <SelectTrigger className="h-9 w-[120px] text-xs">
          <SelectValue placeholder="Font" />
        </SelectTrigger>
        <SelectContent>
          {EMAIL_SAFE_FONTS.map((font) => (
            <SelectItem key={font.value} value={font.value}>
              <span style={{ fontFamily: font.value }}>{font.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={editor.getAttributes('textStyle').fontSize || "12pt"}
        onValueChange={(value) => setFontSize(value)}
      >
        <SelectTrigger className="h-9 w-[70px] text-xs">
          <SelectValue placeholder="Size" />
        </SelectTrigger>
        <SelectContent>
          {EMAIL_SAFE_FONT_SIZES.map((size) => (
            <SelectItem key={size.value} value={size.value}>
              {size.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <ColorPicker
        currentColor={editor.getAttributes('textStyle').color || "#000000"}
        onChange={(color) => editor.chain().focus().setColor(color).run()}
      />

      <Button
        variant="ghost"
        size="sm"
        className={editor.isActive("bold") ? "bg-muted" : ""}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={editor.isActive("italic") ? "bg-muted" : ""}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={editor.isActive("bulletList") ? "bg-muted" : ""}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={editor.isActive("orderedList") ? "bg-muted" : ""}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={editor.isActive("link") ? "bg-muted" : ""}
        onClick={setLink}
      >
        <Link className="h-4 w-4" />
      </Button>
      <ImageUrlButton editor={editor} />

      <Button
        variant="ghost"
        size="sm"
        className={editor.isActive({ textAlign: "left" }) ? "bg-muted" : ""}
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
      >
        <AlignLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={editor.isActive({ textAlign: "center" }) ? "bg-muted" : ""}
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
      >
        <AlignCenter className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={editor.isActive({ textAlign: "right" }) ? "bg-muted" : ""}
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
      >
        <AlignRight className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <SeparatorHorizontal className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        <Redo className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={resetFormatting}
        title="Reset formatting"
      >
        <Eraser className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default TipTapToolbar;
